// Tracks which step image was showing when the visitor last left, so a
// returning visitor's carousel doesn't lead with the same photo again (see
// shuffleImagePool's previousLast param, used in useQuizWizardController.ts).
// Same localStorage-guard shape as lib/analytics/quizStartTracking.ts:
// reads/writes are best-effort and never throw — a visitor with storage
// disabled just falls back to a plain random shuffle every time.

import type { QuizStepKey } from "./QuizStepImage";

const STORAGE_KEY = "bsa_quiz_last_step_image";

function readAll(): Partial<Record<QuizStepKey, string>> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    return parsed as Partial<Record<QuizStepKey, string>>;
  } catch {
    return {};
  }
}

/** Src shown on the visitor's last visit for this step, or undefined if none
 * is recorded (first visit, storage disabled, or a corrupted/stale value —
 * including a leftover numeric value from before this module stored srcs). */
export function readLastShownSrc(step: QuizStepKey): string | undefined {
  const value = readAll()[step];
  return typeof value === "string" ? value : undefined;
}

/** Records the src shown this visit, for next time's exclusion check. Call
 * from an effect, not a useState initializer — see the "where the write
 * happens" note in useQuizWizardController.ts. */
export function writeLastShownSrc(step: QuizStepKey, src: string): void {
  if (typeof window === "undefined") return;
  try {
    const current = readAll();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, [step]: src }));
  } catch {
    // Private-mode Safari or storage disabled — can't persist, so this
    // visit's first image may repeat next time. Degrades to a plain random
    // shuffle for that visitor rather than throwing.
  }
}
